import React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { TbX } from "react-icons/tb";
import { TaskCalendar } from "./TaskCalendar";
import { Loader } from "@/components/map/Loader";
import { useMapContext } from "@/hooks/useMap";
import { useTaskContext } from "@/context/taskContext";
import { zoomToTask } from "@/components/map/taskUtils";
import type { Task } from "@/types/task";

interface CalendarModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tasks: Task[];
  onTaskClick?: (task: Task) => void;
}

export const CalendarModal: React.FC<CalendarModalProps> = ({
  open,
  onOpenChange,
  tasks,
  onTaskClick,
}) => {
  const { mapInstance } = useMapContext();
  const { isUpdating } = useTaskContext();

  const handleTaskClick = (task: Task) => {
    // Zoom to task on map if it has geometry
    if (task.geom && mapInstance?.current) {
      zoomToTask(task, mapInstance.current);
    }

    // Call the original onTaskClick if provided
    onTaskClick?.(task);
  };
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-gray-800/50 bg-opacity-50 z-[1100] pointer-events-none" />
        <Dialog.Content className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-xl w-[95vw] h-[95vh] max-w-7xl z-[1101] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <Dialog.Title
              className="font-['Open_Sans'] font-bold text-[#444444] text-[18px]"
              style={{ fontVariationSettings: "'wdth' 100" }}
            >
              Task Calendar View
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                className="cursor-pointer text-[#828282] hover:text-[#444444] p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                aria-label="Close"
              >
                <TbX size={22} />
              </button>
            </Dialog.Close>
          </div>

          {/* Calendar Content */}
          <div className="flex-1 overflow-auto relative">
            <TaskCalendar tasks={tasks} onTaskClick={handleTaskClick} />

            {/* Loading indicator for task updates (e.g., drag-and-drop) */}
            {isUpdating && <Loader text="Updating Task" />}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
